import { useId, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import {
  Agent,
  AgentStarterPrompt,
  AgentStarterPromptInput,
  MAX_STARTER_PROMPTS,
  STARTER_PROMPT_MAX_LENGTH,
} from "~/services/agents";
import {
  AgentSortableListTab,
  EditFormFooter,
  type EditFormRenderProps,
} from "./AgentSortableListTab";

const toInput = (item: AgentStarterPrompt): AgentStarterPromptInput => ({ text: item.text });

const StarterPromptView = ({ item }: { item: AgentStarterPrompt }) => (
  <p className="min-w-0 flex-1 text-sm text-foreground">{item.text}</p>
);

const StarterPromptEditForm = ({
  initial,
  disabled,
  onSave,
  onCancel,
}: EditFormRenderProps<AgentStarterPromptInput>) => {
  const textId = useId();
  const [text, setText] = useState(initial.text);

  const trimmed = text.trim();
  const canSave = trimmed.length > 0 && trimmed.length <= STARTER_PROMPT_MAX_LENGTH && !disabled;

  const handleSubmit = () => {
    if (!canSave) return;
    onSave({ text: trimmed });
  };

  return (
    <>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={textId}>Prompt</Label>
        <Textarea
          id={textId}
          autoFocus
          rows={3}
          placeholder="e.g. Build a Kaplan-Meier curve for overall survival, stratified by treatment arm…"
          maxLength={STARTER_PROMPT_MAX_LENGTH}
          value={text}
          onChange={(e) => setText(e.target.value)}
          disabled={disabled}
        />
      </div>
      <EditFormFooter
        counterCurrent={text.length}
        counterMax={STARTER_PROMPT_MAX_LENGTH}
        canSave={canSave}
        disabled={disabled}
        onCancel={onCancel}
        onSave={handleSubmit}
      />
    </>
  );
};

export const AgentStarterPromptsTab = () => {
  const agent = useOutletContext<Agent>();
  const canEdit = useCanEditAgent(agent);

  return (
    <AgentSortableListTab<AgentStarterPrompt, AgentStarterPromptInput>
      title="Starter Prompts"
      context="Shown at the start of new conversations with this agent"
      description={`Write up to ${MAX_STARTER_PROMPTS} starter prompts that demonstrate what users can ask of this agent. Drag to reorder — the first prompt appears in the top-left of the conversation start screen.`}
      agent={agent}
      field="starter_prompts"
      items={agent.starter_prompts}
      maxItems={MAX_STARTER_PROMPTS}
      canEdit={canEdit}
      toInput={toInput}
      emptyDraft={{ text: "" }}
      renderView={(item) => <StarterPromptView item={item} />}
      renderEditForm={(props) => <StarterPromptEditForm {...props} />}
      addLabel="Add prompt"
      emptyMessage="No starter prompts yet."
      errorMessage="Failed to save starter prompts"
    />
  );
};
